import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

interface DropZoneProps {
  onFiles(files: File[]): void;
  children(state: { isDragOver: boolean }): ReactNode;
}

/** Drop files anywhere on the page — SPEC.md §6. Listens on window so the
 * whole viewport is a target, not just the bar itself. */
export default function DropZone({ onFiles, children }: DropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const depth = useRef(0);

  useEffect(() => {
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setIsDragOver(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [onFiles]);

  return (
    <>
      {isDragOver && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-40 animate-drag-glow-in opacity-100"
          style={{
            background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(91,140,255,0.08), transparent 70%)',
          }}
        />
      )}
      {children({ isDragOver })}
    </>
  );
}
